import { Sine, TimelineLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import { Emitter } from "pixi-particles";
import { Application } from "../Application";
import * as Constants from "../Data/Constants";
import * as SFX from "../Data/SoundEffectKeys";
import * as Topics from "../Data/Topics";
import * as VoiceOver from "../Data/VoiceOverKeys";
import { InteruptType, VoiceCategory } from "../GameObjects/VoiceOverManager";

export class BalloonSelectable extends HLF.Input.Selectable {
    private static readonly OVERLAY_COLOUR: HLF.Colour = new HLF.Colour(33, 138, 202);
    private static readonly OVERLAY_COLOUR_SPECIAL: HLF.Colour = new HLF.Colour(0, 72, 140);

    private _index: number;
    private _overlayPerc: number;
    private _whiteoutPerc: number;
    private _isSpecial: boolean;

    private _image: PIXI.Sprite;
    private _overlay: PIXI.Sprite;
    private _confetti: Emitter;
    private _sparkles: Emitter;
    private _particleContainer: PIXI.Container;
    private _outline: PIXI.Sprite;
    private _clouds: PIXI.Sprite[] | undefined;
    private _banner: PIXI.Sprite | undefined;
    private _balloons: PIXI.Sprite[] | undefined;

    private _hoverTween: TweenMax;
    private _revealAnim: TimelineLite | undefined;

    constructor(pCallback: () => void, pIndex: number, pUnlocked: boolean, pSpecial: boolean) {
        super();
        this._index = pIndex;
        this._isSelected = false;
        this.onSelected.push(pCallback);
        this._overlayPerc = 1;
        this._whiteoutPerc = 0;
        this._isSpecial = pSpecial;
        this._clickedSfx = SFX.UI_SELECT;

        this._image = HLF.PixiUtils.makeSprite("balloon-0" + pIndex, Constants.SPRITESHEET_GAMEPLAY);
        this._image.visible = false;
        this._visuals.addChild(this._image);

        this._overlay = HLF.PixiUtils.makeSprite("balloon-0" + pIndex + "-flat", Constants.SPRITESHEET_GAMEPLAY);
        this._overlay.scale.set(2);
        this._overlay.tint = this.overlayColour.toHex();
        this._visuals.addChild(this._overlay);

        this._outline = HLF.PixiUtils.makeSprite("balloon-0" + pIndex + "-outline", Constants.SPRITESHEET_GAMEPLAY);
        this._outline.scale.set(2);
        this._outline.visible = false;
        this._visuals.addChild(this._outline);

        if (pSpecial) {
            this._clouds = new Array<PIXI.Sprite>(2);
            for (let i = 0; i < this._clouds.length; ++i) {
                this._clouds[i] = HLF.PixiUtils.makeSprite(
                    "select-special-cloud-0" + i,
                    Constants.SPRITESHEET_UI,
                );
                this._clouds[i].position.set(i === 0 ? 20 : 0,  i === 0 ? -100 : 70);
                this._visuals.addChild(this._clouds[i]);
            }
            this._balloons = new Array<PIXI.Sprite>(2);
            for (let i = 0; i < this._balloons.length; ++i) {
                this._balloons[i] = HLF.PixiUtils.makeSprite(
                    "select-special-balloon-" + (i === 0 ? "left" : "right"),
                    Constants.SPRITESHEET_UI,
                );
                this._balloons[i].position.set(i === 0 ? -100 : 100, -40);
                this._visuals.addChild(this._balloons[i]);
            }
            this._banner = HLF.PixiUtils.makeSprite("select-special-banner", Constants.SPRITESHEET_UI);
            this._banner.position.set(0, 50);
            this._visuals.addChild(this._banner);
        }

        this._particleContainer = new PIXI.Container();
        this.addChild(this._particleContainer);

        const shapes: string[] = [ "rect", "circle", "tri" ];
        const particles: any[] = new Array<any>();
        const sheet: PIXI.loaders.Resource = PIXI.loader.resources[Constants.SPRITESHEET_UI];
        for (let i = 0; i < 4; ++i) {
            for (let j = 0; j < shapes.length; ++j) {
                particles.push(sheet.textures!["particle-confetti-" + shapes[j] + "-0" + i]);
            }
        }

        this._confetti = new Emitter(
            this._particleContainer,
            particles,
            {
                alpha: {
                    list: [
                        { value: 0.1, time: 0 },
                        { value: 0.8, time: 0.1 },
                        { value: 0.8, time: 0.8 },
                        { value: 0.1, time: 1 },
                    ],
                    isStepped: false,
                },
                speed: {
                    list: [
                        { value: 300, time: 0 },
                    ],
                    isStepped: false,
                    minimumSpeedMultiplier: 0.1,
                },
                acceleration: new PIXI.Point(0, 300),
                startRotation: { min: 135, max: 405 },
                rotationSpeed: { min: 0, max: 0 },
                lifetime: { min: 1.0, max: 1.5 },
                frequency: 0.01,
                spawnChance: 1,
                particlesPerWave: 50,
                emitterLifetime: 0.75,
                maxParticles: 50,
                pos: { x: 0, y: 0 },
                addAtBack: false,
                spawnType: "circle",
                spawnCircle: { x: 0, y: 0, r: 30 },
            },
        );
        this._confetti.emit = false;

        this._sparkles = new Emitter(
            this._particleContainer,
            [ sheet.textures!["particle-sparkle"] ],
            {
                alpha: {
                    list: [
                        { value: 0.1, time: 0 },
                        { value: 0.8, time: 0.1 },
                        // { value: 0.8, time: 0.8 },
                        { value: 0.1, time: 1 },
                    ],
                    isStepped: false,
                },
                scale: {
                    list: [
                        { value: 0.4, time: 0.0 },
                        { value: 1.0, time: 0.2 },
                        // { value: 1.0, time: 0.8 },
                        { value: 0.4, time: 1.0 },
                    ],
                },
                rotationSpeed: { min: -80, max: 80 },
                lifetime: { min: 0.75, max: 0.75 },
                frequency: 0.1,
                spawnChance: 1,
                particlesPerWave: 1,
                emitterLifetime: 1.0,
                maxParticles: 50,
                pos: { x: 0, y: 0 },
                addAtBack: false,
                spawnType: "circle",
                spawnCircle: { x: 0, y: 0, r: 120 },
            },
        );
        this._sparkles.emit = false;

        this._outlineFilter.uniforms.thickness = [0.015, 0.015];
        this._outlineFilter.uniforms.outlineColor = [1, 1, 1, 1];

        this.hitArea = new PIXI.Rectangle(
            -100,
            -125,
            200,
            250,
        );
        this.interactive = false;
        this._hoverVo = VoiceOver.SELECT_HOVER_PREFIX + HLF.NumberUtils.addZeroPadding(pIndex, 2);

        if (pUnlocked) {
            this.unlock(true);
        }

        this._hoverTween = TweenMax.to(this._visuals, HLF.Random.floatBetween(3.0, 4.0), {
            y: 10,
            ease: Sine.easeInOut,
            repeat: -1,
            yoyo: true,
        });
        this._hoverTween.totalTime(HLF.Random.floatBetween(0, 4));
    }

    public get isSelected(): boolean {
        return this._isSelected;
    }

    public get overlayPerc(): number {
        return this._overlayPerc;
    }

    public set overlayPerc(pValue: number) {
        this._overlayPerc = pValue;
        // this._overlayFilter.uniforms.uPerc = pValue;
        this._overlay.alpha = pValue;
    }

    public get whiteoutPerc(): number {
        return this._whiteoutPerc;
    }

    public set whiteoutPerc(pValue: number) {
        this._whiteoutPerc = pValue;
        this._overlay.tint = HLF.Colour.lerp(this.overlayColour, HLF.Colour.WHITE, pValue).toHex();
    }

    private get overlayColour(): HLF.Colour {
        return this._isSpecial ? BalloonSelectable.OVERLAY_COLOUR_SPECIAL : BalloonSelectable.OVERLAY_COLOUR;
    }

    public update(pDeltaTime: number): void {
        this._confetti.update(pDeltaTime);
        this._sparkles.update(pDeltaTime);
    }

    public destroy(pOptions: boolean | PIXI.DestroyOptions = { children: true }): void {
        if (this._revealAnim !== undefined) { this._revealAnim.kill(); }
        // As per: https://github.com/pixijs/pixi-particles
        if (
            Application.instance.renderer.plugins.sprite.sprites !== undefined &&
            Application.instance.renderer.plugins.sprite.sprites !== null
        ) {
            Application.instance.renderer.plugins.sprite.sprites.length = 0;
        }
        this._hoverTween.kill();
        super.destroy(pOptions);
    }

    public select(): void {
        super.select();
        this._visuals.filters = [];
        this._outline.visible = true;
        this.interactive = false;
    }

    public deselect(): void {
        super.deselect();
        this._outline.visible = false;
        this.interactive = true;
    }

    public onFocusBegin(): void {
        super.onFocusBegin();
        PubSub.publishSync(Topics.KEYBOARD_FOCUS_BEGIN, this);
    }

    public onFocusEnd(): void {
        super.onFocusEnd();
        PubSub.publishSync(Topics.KEYBOARD_FOCUS_END, this);
    }

    public onFocusActivated(): void {
        super.onFocusActivated();
    }

    public getFocusPosition(): PIXI.Point {
        return this.getGlobalPosition();
    }

    public unlock(pInstantly: boolean = false): void {
        if (this._revealAnim !== undefined) { this._revealAnim.kill(); }
        if (pInstantly) {
            this.interactive = true;
            this._overlay.visible = false;
            this._image.visible = true;
            if (this._isSpecial) {
                this._banner!.visible = false;
                for (let i = 0; i < this._balloons!.length; ++i) {
                    this._balloons![i].visible = false;
                }
                for (let i = 0; i < this._clouds!.length; ++i) {
                    this._clouds![i].visible = false;
                }
            }
        }
        else {
            this._revealAnim = new TimelineLite();
            this._revealAnim.to(this._visuals.scale, 0.5, {
                x: 0.85,
                y: 0.85,
                ease: Sine.easeIn,
            }, 0);
            this._revealAnim.to(this._visuals.scale, 0.25, {
                x: 1.1,
                y: 1.1,
                ease: Sine.easeOut,
            }, 0.5);
            this._revealAnim.call(() => {
                this.select();
            }, undefined, this, 0.5);
            this._revealAnim.to(this._visuals.scale, 0.5, {
                x: 1.0,
                y: 1.0,
                ease: Sine.easeInOut,
            }, 0.75);

            this._revealAnim.to(this, 0.25, {
                whiteoutPerc: 1,
                ease: Sine.easeInOut,
            }, 0.5);
            this._revealAnim.to(this, 0.75, {
                overlayPerc: 0,
                ease: Sine.easeInOut,
            }, 0.75);
            this._revealAnim.call(() => {
                this._image.visible = true;
            }, undefined, this, 0.75);

            this._revealAnim.call(() => {
                this._confetti.emit = true;
                this._sparkles.emit = true;
                Application.instance.audio.play(SFX.CHIME, 1, false, HLF.AudioCategory.SFX);
            }, undefined, this, 0.5);

            this._revealAnim.call(() => {
                Application.instance.voiceOver.play(
                    VoiceOver.SELECT_UNLOCK_PREFIX + 0 + this._index, InteruptType.EQUAL, VoiceCategory.INSTRUCTIONAL);
            }, undefined, this, 0.5);

            if (this._isSpecial) {
                this._revealAnim.to(this._banner!, 0.5, {
                    y: 0,
                    alpha: 0,
                    ease: Sine.easeInOut,
                }, 0.5);
                this._revealAnim.to(this._banner!.scale, 0.5, {
                    x: 1.2,
                    y: 1.2,
                    ease: Sine.easeInOut,
                }, 0.5);
                for (let i = 0; i < this._balloons!.length; ++i) {
                    this._revealAnim.to(this._balloons![i], 0.5, {
                        y: -90,
                        alpha: 0,
                        ease: Sine.easeInOut,
                    }, 0.5);
                    this._revealAnim.to(this._balloons![i].scale, 0.5, {
                        x: 1.2,
                        y: 1.2,
                        ease: Sine.easeInOut,
                    }, 0.5);
                }
                for (let i = 0; i < this._clouds!.length; ++i) {
                    this._revealAnim.to(this._clouds![i], 0.5, {
                        x: i === 0 ? -40 : 60,
                        alpha: 0,
                        ease: Sine.easeInOut,
                    }, 0.5);
                }
            }
        }
    }

    protected playHoverVo(): void {
        if (this._hoverVo !== undefined) {
            Application.instance.voiceOver.play(this._hoverVo, InteruptType.EQUAL, VoiceCategory.UI);
        }
    }
}
